// 月视图日历网格纯函数(UI-SPEC §5.1.b §5.1.c)
// 无第三方依赖/业务契约依赖(刻意不引 dayjs / @ark/mets-shared),便于 shared 包保持轻量。
// SUN 起首周;月首/月末用占位空白格补齐到 7 的倍数。

export type EventCalendarItem = {
  eventSign: string;
  eventDate: number;
  eventType: 'match' | 'concert';
  opponentShort?: string;
  opponentFull?: string;
  teamText?: string;
  status: 'available' | 'hot' | 'sold-out' | 'wish' | 'empty';
  wishlistAdded?: boolean;
};

export type CalendarCell = {
  date: Date;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  event?: EventCalendarItem;
};

export type CellVariant =
  | 'baseball'
  | 'concert'
  | 'concert-hot'
  | 'concert-wish'
  | 'hot'
  | 'sold-out'
  | 'wish'
  | 'empty';

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * 生成月视图网格:SUN 起首,7 列 × N 行,长度必为 7 的倍数。
 * @param year 4 位年份
 * @param month 1-12
 * @param events 本期可兑换场次列表
 */
export function buildMonthGrid(
  year: number,
  month: number,
  events: EventCalendarItem[],
): CalendarCell[] {
  const firstOfMonth = new Date(year, month - 1, 1);
  const firstWeekday = firstOfMonth.getDay(); // 0 = Sun

  const lastOfMonth = new Date(year, month, 0); // 下月 0 号 = 本月最后一天
  const lastWeekday = lastOfMonth.getDay();
  const trailing = 6 - lastWeekday;

  // grid 从本月 1 号向前回拨到周日
  const gridStart = new Date(year, month - 1, 1 - firstWeekday);

  const daysInMonth = lastOfMonth.getDate();
  const totalCells = firstWeekday + daysInMonth + trailing;

  const cells: CalendarCell[] = [];
  for (let i = 0; i < totalCells; i++) {
    // 克隆 gridStart 并 +i 天,避免共享引用
    const d = new Date(
      gridStart.getFullYear(),
      gridStart.getMonth(),
      gridStart.getDate() + i,
    );
    const isCurrent = d.getMonth() === month - 1;
    const event = isCurrent
      ? events.find((e) => sameDay(new Date(e.eventDate * 1000), d))
      : undefined;
    cells.push({
      date: d,
      dayOfMonth: d.getDate(),
      isCurrentMonth: isCurrent,
      event,
    });
  }
  return cells;
}

/**
 * 单元格 variant 判定。
 * 优先级:空白(月外 / 无事件 / sold-out 兜底) > concert 复合状态 > hot > wish > concert > match(棒球样式)
 */
export function cellVariant(cell: CalendarCell): CellVariant {
  if (!cell.isCurrentMonth || !cell.event) return 'empty';
  const ev = cell.event;
  // 本期后端只返回可兑换场次;sold-out 若异常透传到前端,按空白格兜底,
  // 避免展示不可兑换场次误导用户点击。
  if (ev.status === 'sold-out') return 'empty';
  if (ev.eventType === 'concert' && ev.status === 'hot') return 'concert-hot';
  if (ev.eventType === 'concert' && ev.status === 'wish') return 'concert-wish';
  if (ev.status === 'hot') return 'hot';
  if (ev.status === 'wish') return 'wish';
  if (ev.eventType === 'concert') return 'concert';
  return 'baseball';
}
